import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Icon from './Icon';
import { colors } from '../theme/colors';

const Layout = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const tabs = [
    { path: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
    { path: '/saisie', label: 'Saisie', icon: 'saisie' },
    { path: '/liste', label: 'Apprenants', icon: 'liste' },
    { path: '/statistiques', label: 'Statistiques', icon: 'statistiques' },
    { path: '/suivi', label: 'Suivi', icon: 'suivi' },
    { path: '/spc', label: 'SPC', icon: 'spc' },
    { path: '/alertes', label: 'Alertes', icon: 'alertes' },
    { path: '/rapports', label: 'Rapports', icon: 'rapports' },
    { path: '/configuration', label: 'Configuration', icon: 'configuration' }
  ];

  const handleLogout = () => {
    if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      logout();
      window.location.href = '/login';
    }
  };

  const getRoleLabel = (role) => {
    const roles = {
      admin: 'Admin',
      formateur: 'Formateur',
      lecture: 'Lecture'
    };
    return roles[role] || role;
  };

  const getRoleIcon = (role) => {
    const icons = {
      admin: 'admin',
      formateur: 'formateur',
      lecture: 'lecteur'
    };
    return icons[role] || 'user';
  };

  // Masquer l'onglet Saisie pour les lecteurs
  const visibleTabs = user?.role === 'lecture'
    ? tabs.filter(tab => tab.path !== '/saisie')
    : tabs;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b-4 border-red-600 shadow-sm sticky top-0 z-30">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 gradient-red rounded-xl flex items-center justify-center text-white shadow-red">
                <Icon name="home" size={28} color="white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">SIMPLON Bénin</h1>
                <p className="text-red-600 text-sm font-medium">Gestion des Insertions</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden lg:flex items-center space-x-3">
                {/* Les KPIs peuvent être ajoutés ici si nécessaire */}
              </div>
              <div className="flex items-center space-x-3 pl-3 border-l border-gray-200">
                <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center border-2 border-red-100">
                  <Icon name={getRoleIcon(user?.role)} size={20} color={colors.primary[600]} />
                </div>
                <div className="hidden md:block">
                  <p className="text-sm font-semibold text-gray-800">{user?.name || '-'}</p>
                  <p className="text-xs text-gray-500">{getRoleLabel(user?.role)}</p>
                </div>
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors duration-200"
                    aria-label="Menu utilisateur"
                  >
                    <Icon name="configuration" size={20} />
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden z-50">
                      <Link
                        to="/configuration"
                        className="flex items-center px-4 py-3 text-left hover:bg-red-50 text-sm text-gray-700 transition-colors duration-150"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Icon name="lock" size={18} className="mr-3 text-gray-500" />
                        Changer mot de passe
                      </Link>
                      {user?.role === 'admin' && (
                        <Link
                          to="/configuration?tab=users"
                          className="flex items-center px-4 py-3 text-left hover:bg-red-50 text-sm text-gray-700 transition-colors duration-150"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <Icon name="users" size={18} className="mr-3 text-gray-500" />
                          Gérer utilisateurs
                        </Link>
                      )}
                      <hr className="my-1" />
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center px-4 py-3 text-left hover:bg-red-50 text-red-600 text-sm transition-colors duration-150"
                      >
                        <Icon name="logout" size={18} className="mr-3" />
                        Déconnexion
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow-md mb-6 sticky top-[76px] z-20">
        <div className="container mx-auto px-4">
          <div className="flex space-x-1 overflow-x-auto py-2">
            {visibleTabs.map((tab) => {
              const isActive = location.pathname === tab.path;
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap transition-all duration-200 ${isActive
                    ? 'gradient-red text-white shadow-red'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-red-600'
                    }`}
                >
                  <Icon
                    name={tab.icon}
                    size={18}
                    color={isActive ? 'white' : 'currentColor'}
                  />
                  <span>{tab.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-4 pb-8">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;

